import { z } from 'zod'
import { baseEntitySchema, isBaseEntity } from '../../core/entity.js'
import { scaleSchema } from '../music/scale.js'

/**
 * A melody is a standalone, single-voice line the user owns and edits on a piano roll.
 *
 * Timing and feel are first-class: every note carries a grid position (`onset`) AND a signed
 * millisecond distance from that grid line (`offsetMs`). The offset IS the feel - preserved from
 * capture, editable, and applied at playback. This reverses the May 2026 "Melody capture" SPEC,
 * which stored a continuous Hz contour and quantized at render; a contour cannot be a training
 * reference and has no beat to measure timing against.
 *
 * `onset` and `duration` are in QUARTER-NOTE beats, and `bpm` counts quarter notes per minute, so
 * `beatMs = 60000 / bpm` regardless of meter. A bar is `top * 4 / bottom` beats.
 *
 * The AI melody-suggestion shape (`melodyNoteSchema`: note name + enumerated duration) is NOT
 * reused as the store - enumerated durations cannot carry a feel. Converters both ways live in
 * `lib/music-lib/src/melody/convert.ts`.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §2
 */

/** Slot resolution onsets sit on. Note values, not meter units. */
export const melodyGrids = ['8', '16', '8t', '16t'] as const

export const melodyGridSchema = z.enum(melodyGrids)

export type MelodyGrid = z.infer<typeof melodyGridSchema>

/**
 * The Melody Trainer's own meter. Deliberately NOT `meterSchema` from `../music/rhythm.js`: `top`
 * is bounded 1–32 because latency calibration uses `{ top: 1 }` so that every click is a
 * downbeat, and 1 is not in `meterSchema`'s 2–12 union. `MeterSpec` in
 * `@tunasong/music-lib` is a third, also-distinct shape — it carries `grouping` and
 * `accentPositions`, which no time signature has. The three are not foldable into one; folding
 * them breaks the calibration.
 */
export const melodyMeterSchema = z.object({
  top: z.number().int().min(1).max(32).describe('Beats per bar - the meter top number'),
  bottom: z.union([z.literal(4), z.literal(8)]).describe('The note value that gets one beat'),
})

export type MelodyMeter = z.infer<typeof melodyMeterSchema>

export const melodyTempoSchema = z.object({
  bpm: z.number().min(20).max(300).describe('Quarter notes per minute'),
  meter: melodyMeterSchema,
})

export type MelodyTempo = z.infer<typeof melodyTempoSchema>

export const melodyEntityNoteSchema = z.object({
  midi: z.number().int().min(0).max(127).describe('MIDI note number'),
  onset: z.number().min(0).describe('Beats from the start. Always on a grid slot.'),
  duration: z.number().positive().describe('Length in beats, snapped to the grid'),
  velocity: z.number().min(0).max(1).describe('Note velocity, 0-1'),
  offsetMs: z.number().describe('Signed distance in ms from the note grid slot. THE FEEL. Bounded to half a slot.'),
})

export type MelodyEntityNote = z.infer<typeof melodyEntityNoteSchema>

export const melodyFeelSchema = z.object({
  swing: z.number().min(0).max(1).describe('0 is straight, 1 is a full triplet feel. Applies to off-beat 8ths.'),
})

export type MelodyFeel = z.infer<typeof melodyFeelSchema>

export const melodySourceKinds = ['audio', 'midi', 'manual'] as const

export const melodySourceSchema = z.object({
  kind: z.enum(melodySourceKinds).describe('What produced the notes'),
  entityId: z.string().describe('The audio or MIDI capture entity the notes came from').optional(),
})

export type MelodySource = z.infer<typeof melodySourceSchema>

export const melodyInstrumentHints = ['voice', 'piano', 'guitar'] as const

export const melodyInstrumentHintSchema = z.enum(melodyInstrumentHints)

export type MelodyInstrumentHint = z.infer<typeof melodyInstrumentHintSchema>

export const melodySchema = baseEntitySchema.extend({
  type: z.literal('melody'),
  tempo: melodyTempoSchema,
  grid: melodyGridSchema,
  key: scaleSchema
    .describe('A hint: it shades the roll and lets the Trainer transpose. Never a constraint.')
    .optional(),
  notes: z.array(melodyEntityNoteSchema).describe('Ordered by onset, non-overlapping (single voice)'),
  feel: melodyFeelSchema.describe('Global feel applied at playback').optional(),
  source: melodySourceSchema.describe('The entity the notes were captured from').optional(),
  instrumentHint: melodyInstrumentHintSchema.describe('A timbre, not a claim about the instrument').optional(),
})

export type Melody = z.infer<typeof melodySchema>

/**
 * Cheap structural guard, matching `isScore` / `isSong`: it answers "is this element the melody
 * plugin's", which is what `condRender` and the editor ask. Full validation is
 * `melodySchema.safeParse` and the invariant checks in `MelodyLib.validateMelody` - a half-built
 * element being edited must still render.
 */
export function isMelody(node?: unknown): node is Melody {
  return Boolean(isBaseEntity(node) && node.type === 'melody')
}
